import type { FuseResultMatch } from 'fuse.js';
import { isEmpty } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HighlightSearchRange from '@/components/search/highlight-search-range';
import type { FuzzySearchResult, SearchFriend } from '@/hooks/search/use-search-friends';
import intersperse from '@/utils/intersperse';
import SearchListItem from './search-list-item';

interface Props {
  friend: SearchFriend;
}

export default function SearchFriendResultItem({ friend }: Props) {
  const { FriendID, FriendHeadImg, RemarkName, FriendNickName, FuzzySearch } = friend;

  const { titleHighlight, showNicknameInDescription } = useMemo(() => {
    let nicknameResult: FuseResultMatch | undefined;
    let remarknameResult: FuseResultMatch | undefined;
    for (const h of FuzzySearch?.highlightRanges || []) {
      if (h.key === 'FriendNickName' || h.key === 'NickName') {
        nicknameResult = h;
      }
      if (h.key === 'RemarkName') {
        remarknameResult = h;
      }
      if (nicknameResult && remarknameResult) {
        break;
      }
    }
    return {
      titleHighlight:
        (RemarkName && remarknameResult?.indices) ||
        (!RemarkName && FriendNickName && nicknameResult?.indices) ||
        undefined,
      showNicknameInDescription: !!(RemarkName && nicknameResult),
    };
  }, [RemarkName, FriendNickName, FuzzySearch]);

  return (
    <SearchListItem
      avatarImageUrl={FriendHeadImg}
      title={RemarkName || FriendNickName}
      highlight={titleHighlight}
      description={
        <SearchFriendResultDescription
          id={FriendID}
          fuzzySearch={FuzzySearch}
          showNickname={showNicknameInDescription}
        />
      }
    />
  );
}

export function SearchFriendResultDescription({
  id,
  fuzzySearch,
  showTags = true,
  showNickname = true,
}: {
  id: number;
  fuzzySearch?: FuzzySearchResult;
  showTags?: boolean;
  showNickname?: boolean;
}) {
  const { t } = useTranslation();

  if (!fuzzySearch?.highlightRanges) {
    return null;
  }

  const tags: ReactNode[] =
    (showTags &&
      fuzzySearch.highlightRanges
        .filter((range) => Boolean(range.key === 'Tags.TagName' && range.value))
        .map(({ indices, value = '' }) => (
          <HighlightSearchRange
            className='text-primary '
            key={id + value}
            text={value}
            ranges={indices}
          />
        ))) ||
    [];

  const nickname =
    showNickname &&
    fuzzySearch.highlightRanges?.find(
      (range) => (range.key === 'FriendNickName' || range.key === 'NickName') && range.value,
    );

  const HighlightedNickname = nickname && nickname?.value && (
    <>
      {t('common.nickname')}
      {'： '}
      <HighlightSearchRange
        className='text-primary '
        key={id + nickname.value}
        text={nickname.value}
        ranges={nickname.indices}
      />
    </>
  );

  const HighlightTags = isEmpty(tags) ? null : (
    <>
      {t('common.tag')}
      {'： '}
      {intersperse<ReactNode>(tags, <span>{', '}</span>)}
    </>
  );

  return intersperse<ReactNode>([HighlightedNickname, HighlightTags], <span>{' / '}</span>);
}
